import type { StateCreator } from 'zustand'

import type { PrefillPostData } from '~publish/hooks/usePostComposer'
import type { RpcUpdate } from '~publish/legacy/post/types'

import type { AppSlice } from '../appSlice'
import type { CapabilitySlice } from '../capabilitySlice'
import type { ChannelSlice } from '../channelSlice'
import type { DirtySlice } from '../dirtySlice'
import type { EditorSlice } from '../editorSlice'
import type { ModeSlice } from '../modeSlice/modeSlice'
import type { SaveErrorsSlice } from '../saveErrorsSlice'
import { createEmptyChannelVariant, createEmptyOmniDraft } from './types'
import type {
  ChannelVariant,
  ChannelVariantContent,
  OmniAsset,
  OmniDraft,
} from './types'

export interface UploadErrorContext {
  channelService: string
  channelId?: string
  code?: string
}

export interface UploadError {
  id: string
  message: string
  context: UploadErrorContext
  timestamp: number
}

export interface PostState {
  readonly initialPost?: RpcUpdate
  readonly prefillPostData?: PrefillPostData
  selectedTagIds: string[]
  uploadErrors: UploadError[]
  omni: OmniDraft
  /**
   * True while `useOmniBridge` has seeded `omni` from Flux and both mirror
   * directions are installed. Flag-gated readers (`useDraftView`) must fall
   * back to legacy Flux reads while this is false — before the seed runs,
   * `omni` is an empty draft even though Flux holds the user's content.
   */
  omniBridgeReady: boolean
  /** Per-channel authored content, keyed by channelId. */
  channelVariants: Record<string, ChannelVariant>
  /**
   * The per-channel counterpart of `omniBridgeReady`: true once the channel
   * variants have been seeded from Flux and their mirror is installed. Readers
   * fall back to legacy Flux reads while this is false.
   */
  channelBridgeReady: boolean
}

export interface PostActions {
  setSelectedTagIds: (tagIds: string[]) => void
  addUploadError: (error: Omit<UploadError, 'id' | 'timestamp'>) => void
  dismissUploadError: (errorId: string) => void
  clearUploadErrors: (channelService?: string) => void
  setOmniText: (payload: {
    text: string
    urls?: string[]
    unshortenedUrls?: string[]
  }) => void
  /**
   * INVARIANT (bridge phase): the Zustand→Flux mirror only covers fields with
   * wired Zustand writers. The first production caller of this action must
   * land in the same PR that adds `assets` to `installZustandToFluxMirror` —
   * otherwise flag-on writes never reach Flux and the legacy save path reads
   * stale media.
   */
  setOmniAssets: (assets: OmniAsset[]) => void
  /** Same mirror invariant as `setOmniAssets`: first caller ships with its Zustand→Flux mirror line. */
  setOmniAiAssisted: (aiAssisted: boolean) => void
  setOmniDraftId: (draftId: string | null) => void
  setOmniTitle: (title: string | null) => void
  /** Flux-side writers mirror through here so Zustand stays authoritative-capable. */
  syncOmniFromFlux: (fields: Partial<Omit<OmniDraft, 'id'>>) => void
  setOmniBridgeReady: (ready: boolean) => void
  resetOmni: () => void
  /**
   * Flux-side writer for a single channel's variant: merges `fields` into the
   * channel's entry, creating it if absent. The per-channel counterpart of
   * `syncOmniFromFlux`. Per-field Zustand→Flux writers land with the slice that
   * wires each field (same invariant as `setOmniAssets`).
   */
  syncChannelVariantFromFlux: (
    channelId: string,
    fields: Partial<ChannelVariantContent>,
  ) => void
  /** Drop a channel's variant, e.g. when the channel is deselected. */
  removeChannelVariant: (channelId: string) => void
  setChannelBridgeReady: (ready: boolean) => void
  resetChannelVariants: () => void
}

export type PostSlice = PostState & PostActions

export const createPostSlice: StateCreator<
  AppSlice &
    CapabilitySlice &
    ChannelSlice &
    DirtySlice &
    EditorSlice &
    ModeSlice &
    PostSlice &
    SaveErrorsSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  PostSlice
> = (set) => ({
  initialPost: undefined,
  selectedTagIds: [],
  uploadErrors: [],
  omni: createEmptyOmniDraft(),
  omniBridgeReady: false,
  channelVariants: {},
  channelBridgeReady: false,
  setSelectedTagIds: (tagIds): void =>
    set(
      (state) => {
        state.selectedTagIds = tagIds
      },
      undefined,
      'postSlice:setSelectedTagIds',
    ),
  addUploadError: (error): void =>
    set(
      (state) => {
        const isSameError = (existing: UploadError): boolean =>
          existing.message === error.message &&
          existing.context.channelService === error.context.channelService &&
          existing.context.channelId === error.context.channelId &&
          existing.context.code === error.context.code

        const existingIndex = state.uploadErrors.findIndex(isSameError)
        const timestamp = Date.now()

        if (existingIndex !== -1) {
          state.uploadErrors[existingIndex].timestamp = timestamp
          return
        }

        state.uploadErrors.push({
          ...error,
          id: Math.random().toString(36).slice(2),
          timestamp,
        })
      },
      undefined,
      'postSlice:addUploadError',
    ),
  dismissUploadError: (errorId): void =>
    set(
      (state) => {
        state.uploadErrors = state.uploadErrors.filter(
          (error) => error.id !== errorId,
        )
      },
      undefined,
      'postSlice:dismissUploadError',
    ),
  clearUploadErrors: (channelService): void =>
    set(
      (state) => {
        if (channelService) {
          state.uploadErrors = state.uploadErrors.filter(
            (error) => error.context.channelService !== channelService,
          )
        } else {
          state.uploadErrors = []
        }
      },
      undefined,
      'postSlice:clearUploadErrors',
    ),
  setOmniText: ({ text, urls, unshortenedUrls }): void =>
    set(
      (state) => {
        state.omni.text = text
        if (urls !== undefined) state.omni.urls = urls
        if (unshortenedUrls !== undefined)
          state.omni.unshortenedUrls = unshortenedUrls
      },
      undefined,
      'postSlice:setOmniText',
    ),
  setOmniAssets: (assets): void =>
    set(
      (state) => {
        state.omni.assets = assets
      },
      undefined,
      'postSlice:setOmniAssets',
    ),
  setOmniAiAssisted: (aiAssisted): void =>
    set(
      (state) => {
        state.omni.aiAssisted = aiAssisted
      },
      undefined,
      'postSlice:setOmniAiAssisted',
    ),
  setOmniDraftId: (draftId): void =>
    set(
      (state) => {
        state.omni.draftId = draftId
      },
      undefined,
      'postSlice:setOmniDraftId',
    ),
  setOmniTitle: (title): void =>
    set(
      (state) => {
        state.omni.title = title
      },
      undefined,
      'postSlice:setOmniTitle',
    ),
  syncOmniFromFlux: (fields): void =>
    set(
      (state) => {
        Object.assign(state.omni, fields)
      },
      undefined,
      'postSlice:syncOmniFromFlux',
    ),
  setOmniBridgeReady: (ready): void =>
    set(
      (state) => {
        state.omniBridgeReady = ready
      },
      undefined,
      'postSlice:setOmniBridgeReady',
    ),
  resetOmni: (): void =>
    set(
      (state) => {
        state.omni = createEmptyOmniDraft()
      },
      undefined,
      'postSlice:resetOmni',
    ),
  syncChannelVariantFromFlux: (channelId, fields): void =>
    set(
      (state) => {
        const variant =
          state.channelVariants[channelId] ??
          createEmptyChannelVariant(channelId)
        state.channelVariants[channelId] = { ...variant, ...fields }
      },
      undefined,
      'postSlice:syncChannelVariantFromFlux',
    ),
  removeChannelVariant: (channelId): void =>
    set(
      (state) => {
        delete state.channelVariants[channelId]
      },
      undefined,
      'postSlice:removeChannelVariant',
    ),
  setChannelBridgeReady: (ready): void =>
    set(
      (state) => {
        state.channelBridgeReady = ready
      },
      undefined,
      'postSlice:setChannelBridgeReady',
    ),
  resetChannelVariants: (): void =>
    set(
      (state) => {
        state.channelVariants = {}
      },
      undefined,
      'postSlice:resetChannelVariants',
    ),
})
